import {
  Box,
  Button,
  Container,
  Dialog,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { useIsMobile } from "../../../../../utils/Functions";
import { useTranslation } from "react-i18next";
import useUserAppointmentFilters from "../../../hooks/useUserAppointmentFilters";
import DateRange from "../../../../../components/filter/DateRange";
import StatusMultiSelectFilter from "../../../../../components/filter/StatusMultiSelectFilter";
import React, { Fragment } from "react";
import { ThemeContext } from "../../../../../contexts/ThemeContext";
import fabric from "../../../../../assets/png/darkBackground.png";
import RowStack from "../../../../../components/wrappers/RowStack";
import { CancelOutlined } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import ServiceFilter from "../../../../../components/filter/ServiceFilter";
import PetFilter from "../../../../../components/filter/PetFilter";

export default function AppointmentUserFilters({
  statusOptions,
  timeframe: timeFrame,
}) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { mode } = React.useContext(ThemeContext);
  const isDark = mode == "dark";
  const [open, setOpen] = React.useState(false);
  const {
    appointmentParamKeys,
    handleReset,
    currentSort,
    handleSortChange,
    currentStatusValues,
    handleStatusSelectChange,
  } = useUserAppointmentFilters(statusOptions, timeFrame);

  if (isMobile) {
    return (
      <Fragment>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 99,
            width: "100%",
            backgroundColor: "background.default",
            backgroundImage: isDark ? `url(${fabric})` : null,
            mb: 2.5,
          }}
        >
          <RowStack>
            <Button
              color="primary"
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={() => setOpen(true)}
              sx={{ mx: 2, mt: 1 }}
              fullWidth
            >
              {t("Filter")}
            </Button>
          </RowStack>
        </Box>
        <Dialog open={open} onClose={() => setOpen(false)} fullScreen>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <IconButton
              onClick={() => setOpen(false)}
              size="large"
              color="error"
              title={t("Close")}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" flex={1}>
              {t("Filter")}
            </Typography>
          </Stack>

          {/* Filter Content */}
          <Container sx={{ mt: 2 }}>
            {/* Status Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("Status")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <StatusMultiSelectFilter
                statusOptions={statusOptions}
                currentStatusValues={currentStatusValues}
                handleStatusSelectChange={handleStatusSelectChange}
                testId="appointment.user-filters.status-select"
              />
            </Box>
            {/* Service Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("Service")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <ServiceFilter testId="appointment.user-filters.service-select" />
            </Box>
            {/* Pet Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("Pet")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <PetFilter testId="appointment.user-filters.pet-select" />
            </Box>
            {/* Date Range */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("Date")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <DateRange
                margin={0}
                paramsEnd={appointmentParamKeys.to}
                paramsStart={appointmentParamKeys.from}
                pageParam={appointmentParamKeys.page}
                testId="appointment.user-filters.date-range"
              />
            </Box>
            {/* Sort by */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("Sort")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControl size="small" fullWidth>
                <InputLabel>{t("SortByDate")}</InputLabel>
                <Select
                  value={currentSort}
                  label={t("SortByDate")}
                  onChange={(e) => handleSortChange(e.target.value)}
                  SelectDisplayProps={{
                    "data-testid": "appointment.user-filters.sort-select",
                  }}
                >
                  <MenuItem value="asc">{t("Ascending")}</MenuItem>
                  <MenuItem value="desc">{t("Descending")}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Reset Button */}
            <Box sx={{ mt: 4, mb: 2 }}>
              <Button
                startIcon={<CancelOutlined />}
                color="error"
                onClick={handleReset}
                variant="contained"
                fullWidth
                size="large"
                data-testid="appointment.user-filters.reset-button"
              >
                {t("Reset")}
              </Button>
            </Box>
          </Container>
        </Dialog>
      </Fragment>
    );
  }

  return (
    <Container>
      <Grid container spacing={2} alignItems="center">
        <Grid item sm={4} md={4}>
          <StatusMultiSelectFilter
            statusOptions={statusOptions}
            currentStatusValues={currentStatusValues}
            handleStatusSelectChange={handleStatusSelectChange}
            testId="appointment.user-filters.status-select"
          />
        </Grid>
        <Grid item sm={4} md={4}>
          <ServiceFilter testId="appointment.user-filters.service-select" />
        </Grid>
        <Grid item sm={4} md={4}>
          <PetFilter testId="appointment.user-filters.pet-select" />
        </Grid>
        <Grid item sm={4} md={4}>
          <DateRange
            margin={0}
            paramsEnd={appointmentParamKeys.to}
            paramsStart={appointmentParamKeys.from}
            pageParam={appointmentParamKeys.page}
            testId="appointment.user-filters.date-range"
          />
        </Grid>
        <Grid item sm={4} md={4}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t("SortByDate")}</InputLabel>
            <Select
              value={currentSort}
              label={t("SortByDate")}
              onChange={(e) => handleSortChange(e.target.value)}
              SelectDisplayProps={{
                "data-testid": "appointment.user-filters.sort-select",
              }}
            >
              <MenuItem value="asc">{t("Ascending")}</MenuItem>
              <MenuItem value="desc">{t("Descending")}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item sm={4} md={4}>
          <Button
            onClick={handleReset}
            color="error"
            variant="outlined"
            size="medium"
            fullWidth
            data-testid="appointment.user-filters.reset-button"
          >
            {t("Reset")}
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent="space-between"
        sx={{ mt: 3, mb: 2 }}
      ></Grid>
      <Divider sx={{ mb: 1 }} />
    </Container>
  );
}
